import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download,
  CheckCircle2,
  Check,
  ArrowRight,
  Factory,
  TrendingUp,
  Tag,
  ClipboardList,
  CalculatorIcon,
  PencilRuler,
} from "lucide-react";
import ResponsiveImg from "../components/site/ResponsiveImg";
import SeoHead from "../components/site/SeoHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECT_TYPES = ["Residential", "Commercial", "Multi-family", "Other"];

const HERO_CHECKS = [
  "Up to 40% Lower Pricing",
  "Fully Custom Aluminum Systems",
  "Tempered Glass Included as Standard",
  "NFRC & AAMA Certified",
  "No Dealers Markup",
];

const MARKUP_CARDS = [
  {
    Icon: Factory,
    title: "Dealers don't manufacture — they resell",
    body: "Dealers buy from the factory and add their own margin on top.",
  },
  {
    Icon: TrendingUp,
    title: "Every middleman adds markup",
    body: "Distributors, reps, and dealers each take a cut — and the price keeps climbing.",
  },
  {
    Icon: Tag,
    title: "Why pay more for the same performance",
    body: "Same certifications. Same materials. Higher cost.",
  },
];

const ADVANTAGE_LIST = [
  "Any size",
  "Any shape",
  "Oversized openings & corners",
  "Arches & custom grids",
];

const CERT_BADGES = [
  { title: "Tested for energy efficiency & performance", sub: "Performance you can permit" },
  { title: "AAMA Certified Manufacturing", sub: "Manufactured to U.S. standards" },
  { title: "California Title 24 Compliant", sub: "Code-approved systems" },
];

const HOW_STEPS = [
  {
    Icon: ClipboardList,
    n: "1",
    title: "Send plans or specs",
    body: "Upload drawings, sizes, or project details.",
  },
  {
    Icon: PencilRuler,
    n: "2",
    title: "We engineer & price direct from the factory",
    body: "Fully custom aluminum systems — no dealers, no markup.",
  },
  {
    Icon: CalculatorIcon,
    n: "3",
    title: "You save up to 40%",
    body: "Same certifications. Same performance. Lower cost.",
  },
];

export default function GuidePage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    zip_code: "",
    project_type: "Residential",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const prev = document.title;
    document.title =
      "Avoid These 7 Expensive Window & Door Mistakes — Free Guide | ViewLux";
    const upsert = (n, v) => {
      let t = document.querySelector(`meta[name="${n}"]`);
      if (!t) {
        t = document.createElement("meta");
        t.setAttribute("name", n);
        document.head.appendChild(t);
      }
      const p = t.getAttribute("content");
      t.setAttribute("content", v);
      return { t, p };
    };
    const r = upsert("robots", "noindex, nofollow, noarchive, nosnippet");
    const g = upsert("googlebot", "noindex, nofollow");
    return () => {
      document.title = prev;
      if (r.p) r.t.setAttribute("content", r.p);
      else r.t.remove();
      if (g.p) g.t.setAttribute("content", g.p);
      else g.t.remove();
    };
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const source =
        params.get("utm_source") || params.get("source") || document.referrer || "guide_lp";
      const { data } = await axios.post(`${API}/guide/download`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        zip_code: form.zip_code || null,
        project_type: form.project_type || null,
        source,
      });
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          event_category: "guide_download",
          event_label: source,
          value: 1,
        });
      }
      if (typeof window.obApi === "function") window.obApi("track", "Lead");
      if (typeof window.fbq === "function")
        window.fbq("track", "Lead", { content_name: "ViewLux Colorado Guide" });
      setDone({ download_url: data.download_url });
      window.open(data.download_url, "_blank", "noopener,noreferrer");
      toast.success("Your guide is on its way — check your inbox too!");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    const el = document.getElementById("guide-form-top");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="guide-page" className="min-h-screen bg-[#0f2420] text-white font-nunito">
      <SeoHead
        title="The 2026 ViewLux Design Guide — Save up to 40% on Aluminum Windows & Doors"
        description="Free 2026 design guide: thermally broken aluminum windows and doors, factory-direct pricing, certifications, and case studies for Colorado homeowners and architects."
        noindex={true}
      />
      {/* Slim top bar */}
      <header className="bg-[#0f2420] border-b border-[#c99658]/15 sticky top-0 z-40">
        <div className="container-vlx flex items-center justify-between h-16 md:h-20">
          <a href="/" data-testid="guide-logo" className="flex items-center gap-3">
            <img src="/viewlux/logo.png" alt="ViewLux" className="h-10 md:h-12 w-auto" />
            <span className="hidden sm:inline-flex pl-3 border-l border-[#c99658]/30 text-[#e2b880] text-[10px] tracking-[0.4em] uppercase font-raleway">
              Colorado
            </span>
          </a>
          <a
            href="tel:7207703795"
            data-testid="guide-phone"
            className="text-white/85 text-[12px] tracking-[0.18em] font-raleway hover:text-[#e2b880] transition-colors"
          >
            720.770.3795
          </a>
        </div>
      </header>

      {/* HERO + FORM */}
      <section id="guide-form-top" className="bg-[#0f2420]">
        <div className="container-vlx pt-12 md:pt-20 pb-20 md:pb-28 grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-7 order-2 md:order-1">
            <span className="eyebrow eyebrow-light">Free Homeowner Guide</span>
            <div className="mt-4 rule-gold" />
            <h1 className="heading-lg mt-8 text-[34px] md:text-[54px] leading-[1.05] text-white">
              Avoid These <span className="text-[#e2b880]">7 Expensive Mistakes</span> Most Homeowners Make Replacing Windows &amp; Doors
            </h1>
            <p className="mt-6 text-white/55 italic font-cormorant text-[20px] md:text-[24px] leading-[1.35] max-w-xl">
              &ldquo;That contractors never warn you about.&rdquo;
            </p>
            <p className="mt-8 text-white/75 text-[15px] md:text-[16px] leading-[1.9] max-w-xl">
              A no-nonsense field guide from the team at ViewLux Colorado. Read it
              before you sign a single contract.
            </p>

            <ul className="mt-10 space-y-5 max-w-2xl">
              {[
                { k: "1", t: "How to avoid overpaying by up to 40%", b: "Even with \u201Ctrusted\u201D contractors." },
                { k: "2", t: "What most installers leave out", b: "The shortcuts that lead to failed inspections later." },
                { k: "3", t: "3 window types that cost more — but perform worse", b: "Name brands you'd recognize. What to buy instead." },
                { k: "4", t: "How to tell if your quote has hidden fees", b: "The line items that get padded or switched mid-project." },
              ].map((p) => (
                <li key={p.k} className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-9 h-9 mt-0.5 inline-flex items-center justify-center rounded-full border border-[#c99658]/40 text-[#e2b880] font-cormorant italic text-[18px]">
                    {p.k}
                  </span>
                  <div>
                    <div className="text-white text-[15px] md:text-[16px] font-medium leading-[1.4]">{p.t}</div>
                    <p className="text-white/55 text-[13.5px] leading-[1.7] mt-1">{p.b}</p>
                  </div>
                </li>
              ))}
            </ul>

            <ul className="mt-12 space-y-3 max-w-md">
              {HERO_CHECKS.map((c) => (
                <li key={c} className="flex items-center gap-3 text-white text-[14.5px]">
                  <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-[#c99658] text-[#0f2420]">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  {c}
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT — FORM */}
          <div className="md:col-span-5 order-1 md:order-2 md:sticky md:top-24">
            {!done ? (
              <form
                onSubmit={submit}
                data-testid="guide-form"
                className="bg-white text-[#0f2420] p-7 md:p-10 border border-[#c99658]/40"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Download className="w-4 h-4 text-[#7a5b2f]" />
                  <span className="text-[10.5px] tracking-[0.4em] uppercase font-raleway text-[#7a5b2f]">
                    Get The Guide &middot; Free
                  </span>
                </div>
                <h2 className="heading-lg text-[26px] md:text-[32px] leading-[1.1] text-[#0f2420]">
                  Send Me The Guide
                </h2>
                <p className="mt-3 text-neutral-700 text-[14px] leading-[1.7]">
                  Enter your details below &mdash; we&rsquo;ll email the PDF and
                  open your free copy in a new tab.
                </p>

                <div className="mt-6 space-y-4">
                  <Field label="Full Name" name="name" required value={form.name} onChange={update("name")} />
                  <Field label="Email" name="email" type="email" required value={form.email} onChange={update("email")} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Phone (optional)" name="phone" type="tel" value={form.phone} onChange={update("phone")} />
                    <Field label="ZIP code" name="zip" value={form.zip_code} onChange={update("zip_code")} />
                  </div>
                  <div>
                    <label className="block text-[10.5px] tracking-[0.32em] uppercase font-raleway text-[#7a5b2f] mb-1.5">
                      Project Type
                    </label>
                    <select
                      data-testid="guide-project-type"
                      value={form.project_type}
                      onChange={update("project_type")}
                      className="w-full bg-transparent border-b border-[#0f2420]/30 focus:border-[#c99658] outline-none py-2 text-[14px] text-[#0f2420]"
                    >
                      {PROJECT_TYPES.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="guide-submit"
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#0f2420] hover:bg-[#c99658] text-[#e2b880] hover:text-[#0f2420] py-4 text-[11.5px] tracking-[0.32em] uppercase font-raleway font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending…" : (<>Send The Guide <ArrowRight className="w-4 h-4" /></>)}
                </button>

                <p className="mt-4 text-[11px] text-neutral-600 leading-[1.6] text-center">
                  Your details stay private. No spam.
                </p>
              </form>
            ) : (
              <SuccessCard download_url={done.download_url} />
            )}
          </div>
        </div>
      </section>

      {/* HIDDEN MARKUP — cream section */}
      <section className="bg-cream text-[#0f2420]">
        <div className="container-vlx py-24 md:py-32">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow">The Industry Secret</span>
            <div className="mt-4 rule-gold mx-auto" />
            <h2 className="heading-lg mt-8 text-[30px] md:text-[48px] leading-[1.1]">
              There&rsquo;s a hidden{" "}
              <em className="italic font-light text-[#7a5b2f]">30&ndash;40% markup</em>{" "}
              in most aluminum window quotes.
            </h2>
          </div>
          <ul className="mt-16 grid md:grid-cols-3 gap-px bg-[#c99658]/25">
            {MARKUP_CARDS.map((c, i) => (
              <li
                key={c.title}
                data-testid={`markup-card-${i}`}
                className="bg-cream p-8 md:p-10 flex flex-col items-start gap-5"
              >
                <span className="w-12 h-12 inline-flex items-center justify-center rounded-full border border-[#c99658]/40 text-[#7a5b2f]">
                  <c.Icon className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <h3 className="font-cormorant text-[20px] md:text-[22px] text-[#0f2420] leading-[1.25] font-medium">
                  {c.title}
                </h3>
                <p className="text-neutral-700 text-[14px] leading-[1.85]">{c.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FACTORY-DIRECT ADVANTAGE */}
      <section className="bg-white text-[#0f2420]">
        <div className="container-vlx py-24 md:py-32 grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-6">
            <span className="eyebrow">Why ViewLux</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-[28px] md:text-[42px] leading-[1.1]">
              Factory-direct advantage. <br />
              <em className="italic font-light text-[#7a5b2f]">No dealers markup. No limitations.</em>
            </h2>
            <p className="mt-8 text-neutral-700 text-[15px] leading-[1.9] max-w-md">
              We connect builders and homeowners directly to the manufacturing
              source &mdash; for maximum savings without sacrificing quality or
              performance.
            </p>
          </div>
          <ul className="md:col-span-6 grid sm:grid-cols-2 gap-x-6 gap-y-4">
            {ADVANTAGE_LIST.map((a) => (
              <li key={a} className="flex items-center gap-3 text-[#0f2420] text-[15px]">
                <span className="w-6 h-6 inline-flex items-center justify-center rounded-full bg-[#0f2420] text-[#e2b880]">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
                {a}
              </li>
            ))}
          </ul>

          {/* Cert badges */}
          <ul className="md:col-span-12 mt-4 md:mt-8 grid md:grid-cols-3 gap-5">
            {CERT_BADGES.map((c) => (
              <li
                key={c.title}
                className="border border-dashed border-[#c99658]/50 p-6 md:p-8 text-center"
              >
                <div className="w-12 h-12 mx-auto inline-flex items-center justify-center rounded-full bg-[#0f2420] text-[#e2b880]">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div className="mt-4 font-cormorant text-[18px] text-[#0f2420] font-medium leading-[1.3]">
                  {c.title}
                </div>
                <div className="mt-1 text-neutral-600 text-[12.5px]">{c.sub}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CUSTOM WITHOUT LIMITS — dark feature */}
      <section className="bg-[#0f2420] text-white border-y border-[#c99658]/15">
        <div className="container-vlx py-20 md:py-28 text-center">
          <span className="eyebrow eyebrow-light">Custom Without Limits</span>
          <div className="mt-4 rule-gold mx-auto" />
          <h2 className="heading-lg mt-8 text-[34px] md:text-[56px] leading-[1.05] text-white max-w-3xl mx-auto">
            If you can design it, <br />
            <em className="italic font-light text-[#e2b880]">we can fabricate it.</em>
          </h2>
          <p className="mt-8 text-[11px] tracking-[0.5em] uppercase font-raleway text-[#e2b880]">
            All Aluminum &middot; All Custom
          </p>
          <p className="mt-6 text-white/70 text-[15px] leading-[1.85] max-w-2xl mx-auto">
            Oversized openings. Pocket &amp; multi-slide systems. Arches.
            Corner units. Modern grid patterns. Fully customizable in shape,
            size, and finish &mdash; built to your project, the way you drew
            it.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-cream text-[#0f2420]">
        <div className="container-vlx py-24 md:py-32">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">The Process</span>
            <div className="mt-4 rule-gold mx-auto" />
            <h2 className="heading-lg mt-8 text-[30px] md:text-[44px] leading-[1.1]">
              How does it work?
            </h2>
          </div>
          <ol className="mt-16 grid md:grid-cols-3 gap-6 md:gap-8">
            {HOW_STEPS.map((s) => (
              <li
                key={s.n}
                data-testid={`how-step-${s.n}`}
                className="bg-white p-8 md:p-10 border border-[#c99658]/20 text-center"
              >
                <div className="w-14 h-14 mx-auto inline-flex items-center justify-center rounded-full border border-[#c99658]/40 text-[#7a5b2f]">
                  <s.Icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div className="mt-6 text-[10.5px] tracking-[0.45em] uppercase font-raleway text-[#7a5b2f]">
                  Step 0{s.n}
                </div>
                <h3 className="heading-lg mt-3 text-[20px] md:text-[24px] text-[#0f2420] leading-[1.2]">
                  {s.title}
                </h3>
                <p className="mt-4 text-neutral-700 text-[14px] leading-[1.85]">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SECONDARY CTA — Instant Quote + Free Guide */}
      <section className="bg-white text-[#0f2420]">
        <div className="container-vlx py-24 md:py-32 grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-7">
            <span className="eyebrow">Ready to Compare?</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-[30px] md:text-[44px] leading-[1.1]">
              Get an instant{" "}
              <em className="italic font-light text-[#7a5b2f]">factory quote + free guide.</em>
            </h2>
            <p className="mt-6 text-neutral-700 text-[15px] leading-[1.9] max-w-md">
              No obligation. No middlemen. No inflated pricing. Send us a
              sketch or basic specs and we&rsquo;ll quote it direct.
            </p>
            <ul className="mt-8 space-y-3 max-w-md">
              {HERO_CHECKS.map((c) => (
                <li key={c} className="flex items-center gap-3 text-[#0f2420] text-[14.5px]">
                  <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-[#c99658] text-[#0f2420]">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  {c}
                </li>
              ))}
            </ul>
            <button
              onClick={scrollToForm}
              data-testid="secondary-cta"
              className="mt-10 inline-flex items-center justify-center gap-3 bg-[#0f2420] hover:bg-[#c99658] text-[#e2b880] hover:text-[#0f2420] py-4 px-8 text-[11.5px] tracking-[0.32em] uppercase font-raleway font-semibold transition-colors"
            >
              Get An Instant Quote + Free Guide <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-2 bg-[#c99658]/15 blur-2xl" aria-hidden="true" />
              <ResponsiveImg
                src="/viewlux/docs/guide-cover.jpg"
                alt="The 7 Expensive Mistakes Homeowners Make — Free Guide"
                sizes="(max-width: 768px) 80vw, 400px"
                className="relative w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#0f2420] border-t border-[#c99658]/15">
        <div className="container-vlx py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] tracking-[0.2em] uppercase font-raleway text-white/40">
          <span>&copy; ViewLux Colorado</span>
          <span>8245 I-25 Frontage Rd, Frederick, CO</span>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required }) {
  return (
    <div>
      <label
        htmlFor={`guide-${name}`}
        className="block text-[10.5px] tracking-[0.32em] uppercase font-raleway text-[#7a5b2f] mb-1.5"
      >
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={`guide-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`guide-${name}`}
        className="w-full bg-transparent border-b border-[#0f2420]/30 focus:border-[#c99658] outline-none py-2 text-[14px] text-[#0f2420] placeholder:text-neutral-400"
      />
    </div>
  );
}

function SuccessCard({ download_url }) {
  return (
    <div
      data-testid="guide-success"
      className="bg-white text-[#0f2420] p-8 md:p-10 border border-[#c99658]/40 text-center"
    >
      <CheckCircle2 className="w-14 h-14 text-[#c99658] mx-auto" strokeWidth={1.4} />
      <h2 className="heading-lg mt-6 text-[26px] md:text-[32px] leading-[1.15] text-[#0f2420]">
        Your guide is on its way.
      </h2>
      <p className="mt-3 text-neutral-700 text-[14px] leading-[1.8]">
        We&rsquo;ve also sent a copy to your inbox.
      </p>
      <a
        href={download_url}
        download
        data-testid="guide-redownload"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex items-center justify-center gap-3 bg-[#0f2420] hover:bg-[#c99658] text-[#e2b880] hover:text-[#0f2420] py-3 px-7 text-[11px] tracking-[0.32em] uppercase font-raleway font-semibold transition-colors"
      >
        <Download className="w-4 h-4" /> Open The Guide
      </a>
      <p className="mt-8 text-[12px] text-neutral-600">
        Questions?{" "}
        <a href="tel:7207703795" className="text-[#7a5b2f] underline">
          720.770.3795
        </a>{" "}
        ·{" "}
        <a href="mailto:sales@viewluxcolorado.com" className="text-[#7a5b2f] underline">
          sales@viewluxcolorado.com
        </a>
      </p>
    </div>
  );
}
